import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';
import useWebSocket from './useWebSocket';
import ExcelJS from 'exceljs';
import { fetchMergedAdsbAircraft } from './adsbExchange';
import { getAircraftModex } from './aircraftModex';
import {
    getAircraftTmsName,
    getGroupPolicyLabel,
    getTmsGroups,
} from './groupPolicy';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        status: {
            UP: '#28a745',
            PMC: '#538DD5',
            XC: '#20c997',
            DN: '#dc3545',
            DS: '#dc3545',
            PM: '#ffc107',
            FCF: '#8716bbff',
            AWP: '#335d80ff',
            OTH: '#6c757d',
            DET: '#6c757d',
            AIMD: '#6c757d',
            ACI: '#6c757d',
            REI: '#6c757d'
        }
    };

   return {
        bg: colors.bg,
        text: colors.text,
        subtleText: colors.subtleText,
        border: colors.border,
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1600px', margin: '0 auto', fontFamily: 'sans-serif' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.text,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.text,
        },
        board: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' },
        card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '15px', boxShadow: `0 2px 5px ${colors.shadow}` },
        cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '10px', marginBottom: '10px' },
        buno: { fontSize: '20px', fontWeight: 'bold' },
        statusBadge: { padding: '5px 10px', borderRadius: '12px', color: 'white', fontWeight: 'bold' },
        statusSelect: { padding: '5px 10px', borderRadius: '12px', color: 'white', fontWeight: 'bold', border: 'none', appearance: 'none', cursor: 'pointer' },
        cardBody: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' },
        detail: { color: colors.subtleText },
        reason: { gridColumn: '1 / -1', color: colors.text, fontStyle: 'italic', marginTop: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
        cardFooter: { marginTop: '15px', paddingTop: '10px', borderTop: `1px solid ${colors.border}`, fontSize: '12px', color: colors.subtleText, textAlign: 'right' },
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modal: { backgroundColor: colors.cardBg, padding: '20px', borderRadius: '8px', width: '500px' },
        formGroup: { marginBottom: '15px' },
        label: { display: 'block', marginBottom: '5px' },
        input: { width: '100%', padding: '8px', boxSizing: 'border-box', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text },
        button: { padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' },
        link: colors.link, 
        status: colors.status,
        // Table styles
        tableContainer: { backgroundColor: colors.cardBg, borderRadius: '8px', overflow: 'hidden', boxShadow: `0 2px 5px ${colors.shadow}` },
        table: { width: '100%', borderCollapse: 'collapse' },
        tableHeader: { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', padding: '15px 10px', borderBottom: `2px solid ${colors.border}` },
        tableHeaderCell: { padding: '12px 10px', textAlign: 'left', fontWeight: 'bold', color: colors.text, cursor: 'pointer', userSelect: 'none', position: 'relative' },
        sortableHeader: { display: 'flex', alignItems: 'center', gap: '5px' },
        sortIcon: { fontSize: '12px', opacity: 0.7 },
        tableRow: { borderBottom: `1px solid ${colors.border}`, '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } },
        tableCell: { padding: '12px 10px', color: colors.text, verticalAlign: 'top' },
        controlsContainer: { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
        searchInput: { padding: '8px 12px', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text, minWidth: '200px' },
        filterSelect: { padding: '8px 12px', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text },
        reasonCell: { maxWidth: '200px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
        internalCommentsCell: { maxWidth: '200px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
        pmStatusCell: { maxWidth: '200px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
        // LFD Color Legend styles
        lfdKey: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`
        },
        lfdKeyTitle: { fontSize: '16px', fontWeight: 'bold', color: colors.text, marginBottom: '10px' },
        lfdKeyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' },
        lfdKeyItem: { display: 'flex', alignItems: 'center', gap: '10px' },
        lfdKeyColor: { width: '20px', height: '20px', borderRadius: '4px', border: `1px solid ${colors.border}` },
        lfdKeyText: { fontSize: '14px', color: colors.text },
        // Days Since LFD cell styles
        daysLfdNormal: { color: colors.text },
        daysLfdWarning: { 
            backgroundColor: isDark ? '#4a3a1a' : '#fff3e0',
            color: isDark ? '#ffe0b2' : '#f57c00',
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '4px'
        },
        daysLfdHighlighted: { 
            backgroundColor: isDark ? '#4a1a1a' : '#ffebee',
            color: isDark ? '#ffcdd2' : '#d32f2f',
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '4px'
        },
        daysLfdCritical: { 
            backgroundColor: isDark ? '#1a1a1a' : '#f3e5f5',
            color: isDark ? '#00e5ff' : '#7b1fa2',
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '4px'
        },
        inFlightBadge: {
            display: 'inline-block',
            padding: '5px 10px',
            borderRadius: '12px',
            backgroundColor: '#007bff',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px',
            textDecoration: 'none'
        },
        notInFlightText: {
            color: colors.subtleText,
            fontSize: '12px'
        },
    };
};

// Add CSS for pulse animation
const pulseKeyframes = `
    @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
    }
`;

// Inject the CSS
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
}

const normalizeAircraftIdentifier = (value) => {
    if (!value) return '';
    return String(value).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const getAdsbIdentifierCandidates = (aircraft) => {
    const rawCandidates = [
        aircraft.buno,
        aircraft.callsign,
        aircraft.registration,
        aircraft.hex_id
    ];

    const candidates = new Set();
    rawCandidates.forEach(value => {
        const normalized = normalizeAircraftIdentifier(value);
        if (!normalized) return;
        candidates.add(normalized);

        const digits = normalized.replace(/\D/g, '');
        if (digits.length >= 6) {
            candidates.add(digits);
        }
    });

    return candidates;
};

const isAdsbAircraftInFlight = (aircraft) => {
    if (aircraft.is_on_ground !== null && aircraft.is_on_ground !== undefined) {
        return !aircraft.is_on_ground;
    }

    if (aircraft.altitude !== null && aircraft.altitude !== undefined) {
        return Number(aircraft.altitude) >= 100;
    }

    if (aircraft.ground_speed !== null && aircraft.ground_speed !== undefined) {
        return Number(aircraft.ground_speed) > 50;
    }

    return false;
};


// Column Settings Modal Component
const ColumnSettingsModal = ({ columns, onClose, onSave, theme, canViewRestricted, canEdit }) => {
    const styles = getStyles(theme);
    const [localColumns, setLocalColumns] = useState([...columns]);

    const handleToggleVisibility = (columnId) => {
        setLocalColumns(prev => prev.map(col => 
            col.id === columnId ? { ...col, visible: !col.visible } : col
        ));
    };

    const handleMove = (columnId, direction) => {
        setLocalColumns(prev => {
            const index = prev.findIndex(col => col.id === columnId);
            if (index === -1) return prev;
            if (direction === 'up' && index === 0) return prev;
            if (direction === 'down' && index === prev.length - 1) return prev;
            
            const newColumns = [...prev];
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
            return newColumns;
        });
    };

    const handleReset = () => {
        const defaultColumns = getDefaultColumns(canViewRestricted, canEdit);
        setLocalColumns(defaultColumns);
    };

    const handleSave = () => {
        onSave(localColumns);
        onClose();
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ marginTop: 0 }}>Customize Columns</h2>
                <div style={{ marginBottom: '20px', fontSize: '14px', color: styles.subtleText }}>
                    Drag to reorder, check/uncheck to show/hide columns
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                    {localColumns.map((col, index) => (
                        <div key={col.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px',
                            borderBottom: `1px solid ${styles.border}`,
                            backgroundColor: col.visible ? 'transparent' : styles.bg
                        }}>
                            <input
                                type="checkbox"
                                checked={col.visible}
                                onChange={() => handleToggleVisibility(col.id)}
                                style={{ cursor: 'pointer' }}
                            />
                            <span style={{ flex: 1, color: col.visible ? styles.text : styles.subtleText }}>
                                {col.label}
                            </span>
                            <button
                                onClick={() => handleMove(col.id, 'up')}
                                disabled={index === 0}
                                style={{
                                    ...styles.button,
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    opacity: index === 0 ? 0.5 : 1,
                                    cursor: index === 0 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ↑
                            </button>
                            <button
                                onClick={() => handleMove(col.id, 'down')}
                                disabled={index === localColumns.length - 1}
                                style={{
                                    ...styles.button,
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    opacity: index === localColumns.length - 1 ? 0.5 : 1,
                                    cursor: index === localColumns.length - 1 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ↓
                            </button>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={handleReset} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Reset to Default
                    </button>
                    <button onClick={handleSave} style={{...styles.button, backgroundColor: styles.link, color: 'white'}}>
                        Save
                    </button>
                    <button onClick={onClose} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExportColumnsModal = ({ columns, onClose, onExport, theme, canViewRestricted }) => {
    const styles = getStyles(theme);
    const exportableColumns = columns.filter(col =>
        col.id !== 'actions' &&
        (!col.restricted || canViewRestricted)
    );
    const [selectedColumnIds, setSelectedColumnIds] = useState(
        exportableColumns
            .filter(col => col.visible)
            .map(col => col.id)
    );

    const handleToggleColumn = (columnId) => {
        setSelectedColumnIds(prev =>
            prev.includes(columnId)
                ? prev.filter(id => id !== columnId)
                : [...prev, columnId]
        );
    };

    const handleSelectAll = () => {
        setSelectedColumnIds(exportableColumns.map(col => col.id));
    };

    const handleClearAll = () => {
        setSelectedColumnIds([]);
    };

    const handleExport = () => {
        onExport(selectedColumnIds);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ marginTop: 0 }}>Select Columns to Export</h2>
                <div style={{ marginBottom: '20px', fontSize: '14px', color: styles.subtleText }}>
                    Choose the columns to include in the Excel file. Columns export in the current board order.
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <button onClick={handleSelectAll} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Select All
                    </button>
                    <button onClick={handleClearAll} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Clear All
                    </button>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                    {exportableColumns.map(col => (
                        <label key={col.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px',
                            borderBottom: `1px solid ${styles.border}`,
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={selectedColumnIds.includes(col.id)}
                                onChange={() => handleToggleColumn(col.id)}
                                style={{ cursor: 'pointer' }}
                            />
                            <span style={{ color: styles.text }}>{col.label}</span>
                        </label>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleExport}
                        disabled={selectedColumnIds.length === 0}
                        style={{
                            ...styles.button,
                            backgroundColor: selectedColumnIds.length === 0 ? '#6c757d' : '#17a2b8',
                            color: 'white',
                            opacity: selectedColumnIds.length === 0 ? 0.6 : 1,
                            cursor: selectedColumnIds.length === 0 ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Export
                    </button>
                    <button onClick={onClose} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Modal Component for editing aircraft details (reason, EDD, ECD)
const EditModal = ({ aircraft, onClose, onSave, theme }) => {
    const styles = getStyles(theme);
    const [formData, setFormData] = useState({
        reason: aircraft.reason || '',
        internal_comments: aircraft.internal_comments || '',
        edd: aircraft.edd || null,
        ecd: aircraft.ecd || null,
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        // For date fields, convert empty strings to null
        const processedValue = (name === 'edd' || name === 'ecd') && value === '' ? null : value;
        setFormData({ ...formData, [name]: processedValue });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(aircraft.aircraft, formData);
        onClose();
    };

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h2>Edit Details for {aircraft.buno}</h2>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}><label style={styles.label}>Reason/Remarks</label><textarea style={styles.input} name="reason" value={formData.reason} onChange={handleChange} rows="3"></textarea></div>
                    <div style={styles.formGroup}><label style={styles.label}>Internal Comments</label><textarea style={styles.input} name="internal_comments" value={formData.internal_comments} onChange={handleChange} rows="3"></textarea></div>
                    <div style={styles.formGroup}><label style={styles.label}>EDD (YYYY-MM-DD)</label><input style={styles.input} type="date" name="edd" value={formData.edd || ''} onChange={handleChange} /></div>
                    <div style={styles.formGroup}><label style={styles.label}>ECD (YYYY-MM-DD)</label><input style={styles.input} type="date" name="ecd" value={formData.ecd || ''} onChange={handleChange} /></div>
                    <button type="submit" style={{ ...styles.button, backgroundColor: styles.link, color: 'white' }}>Save Changes</button>
                    <button type="button" onClick={onClose} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>Cancel</button>
                </form>
            </div>
        </div>
    );
};




// Helper function to get default column configuration
const getDefaultColumns = (canViewRestricted, canEdit) => {
    return [
        { id: 'status', label: 'Status', visible: true, field: 'status', sortable: false },
        { id: 'buno', label: 'BUNO', visible: true, field: 'buno', sortable: true },
        { id: 'in_flight', label: 'In Flight', visible: true, field: 'in_flight', sortable: false },
        { id: 'site', label: 'Site', visible: true, field: 'site', sortable: false },
        { id: 'reason', label: 'Reason/Remarks', visible: true, field: 'reason', sortable: false },
        { id: 'internal_comments', label: 'Internal Comments', visible: canViewRestricted, field: 'internal_comments', sortable: false, restricted: true },
        { id: 'edd', label: 'EDD', visible: true, field: 'edd', sortable: true },
        { id: 'ecd', label: 'ECD', visible: true, field: 'ecd', sortable: true },
        { id: 'lfd', label: 'LFD', visible: true, field: 'last_fly_date', sortable: true },
        { id: 'days_lfd', label: 'Days Since LFD', visible: canViewRestricted, field: 'days_lfd', sortable: false, restricted: true },
        { id: 'pm_status', label: 'PM Status', visible: true, field: 'pm_status', sortable: false },
        { id: 'last_updated', label: 'Last Updated', visible: canViewRestricted, field: 'last_updated_by_user', sortable: false, restricted: true },
        { id: 'actions', label: 'Actions', visible: canEdit, field: 'actions', sortable: false, restricted: true }
    ];
};

const AIRCRAFT_STATUS_OPTIONS = ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI'];

function AircraftStatusBoard({ theme }) {
    const styles = getStyles(theme);
    const [searchParams] = useSearchParams();
    const [aircraftList, setAircraftList] = useState([]);
    const [adsbAircraft, setAdsbAircraft] = useState([]);
    const [sites, setSites] = useState([]);
    const [tmsList, setTmsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingAircraft, setEditingAircraft] = useState(null);
    const [showColumnSettings, setShowColumnSettings] = useState(false);
    const [showExportColumns, setShowExportColumns] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const { isStaff, groups } = useAuth();
    // Track pending status updates to prevent WebSocket from overwriting optimistic updates
    const pendingStatusUpdatesRef = React.useRef(new Map()); // Map<aircraftId, { status, oth_sub_status, timestamp }>
    const PENDING_STATUS_PRESERVE_MS = 10000;

    // WebSocket connection for real-time updates
    const wsUrl = useMemo(() => {
        const path = '/ws/aircraft-status/';
        const envWsBase = process.env.REACT_APP_WS_BASE_URL;
        if (envWsBase) {
            try {
                const base = envWsBase.endsWith('/') ? envWsBase.slice(0, -1) : envWsBase;
                if (base.startsWith('ws://') || base.startsWith('wss://')) {
                    return `${base}${path}`;
                }
                if (base.startsWith('http://') || base.startsWith('https://')) {
                    const u = new URL(base);
                    const proto = u.protocol === 'https:' ? 'wss' : 'ws';
                    return `${proto}://${u.host}${path}`;
                }
                const protoFromPage = window.location.protocol === 'https:' ? 'wss' : 'ws';
                return `${protoFromPage}://${base}${path}`;
            } catch (_) {}
        }
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        if (apiBase && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
            try {
                const u = new URL(apiBase);
                const proto = u.protocol === 'https:' ? 'wss' : 'ws';
                return `${proto}://${u.host}${path}`;
            } catch (_) {}
        }
        const protoFromPage = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
        if (isLocal) {
            return `${protoFromPage}://${window.location.hostname}:8000${path}`;
        }
        // Same-origin fallback. Deployments with a separate API domain should
        // set REACT_APP_WS_BASE_URL or REACT_APP_API_BASE_URL at build time.
        return `${protoFromPage}://${window.location.host}${path}`;
    }, []); // Memoize the base URL to prevent re-creation on every render
    
    const { isConnected, lastMessage, error: wsError } = useWebSocket(wsUrl);

    // Search, sort, and filter state
    const [searchTerm, setSearchTerm] = useState(() => (searchParams.get('buno') || '').trim());
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [statusFilter, setStatusFilter] = useState(() => {
        const status = (searchParams.get('status') || '').trim();
        return AIRCRAFT_STATUS_OPTIONS.includes(status) ? status : '';
    });
    const [siteFilter, setSiteFilter] = useState(() => (searchParams.get('site') || '').trim());
    const [tmsFilter, setTmsFilter] = useState('');
    const [excludeDepo, setExcludeDepo] = useState(() => searchParams.get('excludeDepo') === '1');

    useEffect(() => {
        const buno = (searchParams.get('buno') || '').trim();
        if (buno) {
            setSearchTerm(buno);
        }
        const status = (searchParams.get('status') || '').trim();
        if (AIRCRAFT_STATUS_OPTIONS.includes(status)) {
            setStatusFilter(status);
        }
        const site = (searchParams.get('site') || '').trim();
        if (site) {
            setSiteFilter(site);
        }
        setExcludeDepo(searchParams.get('excludeDepo') === '1');
    }, [searchParams]);

    const statusOptions = AIRCRAFT_STATUS_OPTIONS;

    const getStatusSelectValue = (ac) => {
        if (ac.status === 'OTH' && ac.oth_sub_status === 'DEPO') return 'OTH:DEPO';
        return ac.status || '';
    };

    const parseStatusSelection = (value) => {
        if (value === 'OTH:DEPO') return { status: 'OTH', oth_sub_status: 'DEPO' };
        if (value === 'OTH') return { status: 'OTH', oth_sub_status: null };
        return { status: value, oth_sub_status: null };
    };

    const renderStatusOptions = () => statusOptions.map(opt => {
        if (opt === 'OTH') {
            return (
                <optgroup key="OTH" label="OTH">
                    <option value="OTH">OTH</option>
                    <option value="OTH:DEPO">DEPO</option>
                </optgroup>
            );
        }
        return <option key={opt} value={opt}>{opt}</option>;
    });

    const fetchAdsbAircraft = useCallback(async () => {
        try {
            const merged = await fetchMergedAdsbAircraft();
            setAdsbAircraft(merged);
        } catch (error) {
            console.error('Error fetching ADS-B aircraft data:', error);
            setAdsbAircraft([]);
        }
    }, []);

    // Helper function to normalize modex for comparison (handles leading zeros)
    // This ensures "011" matches "11" and "010" matches "10", etc.
    const normalizeModex = (modex) => {
        if (!modex) return '';
        // Convert to string and remove leading zeros, but keep at least one digit
        const normalized = String(modex).replace(/^0+/, '') || '0';
        return normalized;
    };

    // Filter and sort aircraft data
    // NOTE: Backend already filters aircraft based on user's group permissions
    // This frontend filter only further narrows down the already-filtered results
    const filteredAndSortedAircraft = aircraftList
        .filter(aircraft => {
            const matchesSearch = searchTerm === '' ||
                aircraft.buno.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getAircraftModex(aircraft).toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === '' || aircraft.status === statusFilter;
            const matchesSite = siteFilter === '' || aircraft.site === siteFilter;
            const matchesTms = tmsFilter === '' || getAircraftTmsName(aircraft) === tmsFilter;
            const matchesDepo = !excludeDepo || !(aircraft.status === 'OTH' && aircraft.oth_sub_status === 'DEPO');
            return matchesSearch && matchesStatus && matchesSite && matchesTms && matchesDepo;
        })
        .sort((a, b) => {
            if (!sortField) return 0;
            
            let aValue = a[sortField];
            let bValue = b[sortField];
            
            // Handle date sorting for EDD, ECD, and Last Fly Date
            if (sortField === 'edd' || sortField === 'ecd' || sortField === 'last_fly_date') {
                aValue = aValue ? new Date(aValue) : new Date(0);
                bValue = bValue ? new Date(bValue) : new Date(0);
            }
            
            // Handle string sorting
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const fetchData = useCallback(() => {
        setLoading(true);
        
        // Fetch both aircraft status and scheduled inspections data
        Promise.all([
            api.get(`/aircraft-status/`),
            api.get(`/scheduled-inspections/`)
        ])
        .then(([aircraftRes, scheduledRes]) => {
            // Handle paginated response for aircraft data
            let aircraftData = aircraftRes.data;
            if (aircraftRes.data.results) {
                aircraftData = aircraftRes.data.results;
                console.log('AircraftStatusBoard - Paginated response - total count:', aircraftRes.data.count);
            }
            
            // Handle paginated response for scheduled inspections data
            let scheduledData = scheduledRes.data;
            if (scheduledRes.data.results) {
                scheduledData = scheduledRes.data.results;
            }
            
            // Ensure we have arrays
            const aircraftList = Array.isArray(aircraftData) ? aircraftData : [];
            const scheduledInspections = Array.isArray(scheduledData) ? scheduledData : [];

            // Calculate PM status for each aircraft
            const aircraftWithPMStatus = aircraftList.map(aircraft => {
                // Find scheduled inspections for this aircraft by matching modex
                const aircraftModex = getAircraftModex(aircraft);
                const normalizedAircraftModex = normalizeModex(aircraftModex);
                const aircraftInspections = scheduledInspections.filter(inspection => {
                    if (!inspection.modex) return false;
                    const normalizedInspectionModex = normalizeModex(inspection.modex);
                    // Compare normalized values (handles "011" === "11" case)
                    return normalizedInspectionModex === normalizedAircraftModex;
                });
                
                if (aircraftInspections.length === 0) {
                    return { ...aircraft, pm_status: aircraft.pm_status || '' };
                }
                
                // Calculate hours remaining for each inspection: schd_expndtr - tsn
                const inspectionsWithHours = aircraftInspections.map(inspection => {
                    const hoursRemaining = (inspection.schd_expndtr || 0) - (inspection.tsn || 0);
                    return {
                        ...inspection,
                        hoursRemaining: hoursRemaining,
                        displayText: `${inspection.ph_step_nm || 'INSPECTION'} ${hoursRemaining >= 0 ? '+' : ''}${hoursRemaining.toFixed(1)} HRS`
                    };
                });
                
                // Sort by hours remaining (lowest/most urgent first)
                inspectionsWithHours.sort((a, b) => a.hoursRemaining - b.hoursRemaining);
                
                // Use the most urgent inspection for PM status
                const mostUrgent = inspectionsWithHours[0];
                const pmStatus = mostUrgent ? mostUrgent.displayText : (aircraft.pm_status || '');
                
                return { ...aircraft, pm_status: pmStatus };
            });
            
            // Note: The backend automatically filters aircraft based on user's group policy:
            // - Only Staff users see all aircraft
            // - Other users see aircraft for site groups and/or TMS groups assigned to them
            setAircraftList(aircraftWithPMStatus);
        })
        .catch(err => setAlert({ show: true, message: 'Failed to fetch aircraft data.', type: 'error' }))
        .finally(() => setLoading(false));
    }, []); // Empty dependency array since this function doesn't depend on any props or state

    // Handle WebSocket messages for real-time updates.
    // PM hours are already on aircraft_data from the server. Do not GET
    // /scheduled-inspections/ again after the socket is connected.
    useEffect(() => {
        if (lastMessage) {
            switch (lastMessage.type) {
                case 'aircraft_data': {
                    const aircraftWithPMStatus = Array.isArray(lastMessage.data) ? lastMessage.data : [];
                    // Preserve recent pending status updates so full-list refreshes don't revert optimistic changes.
                    const now = Date.now();
                    const protectedList = aircraftWithPMStatus.map(ac => {
                        const pendingUpdate = pendingStatusUpdatesRef.current.get(ac.aircraft);
                        const statusChanged = pendingUpdate && pendingUpdate.status !== ac.status;
                        const subStatusChanged = pendingUpdate && pendingUpdate.oth_sub_status !== ac.oth_sub_status;
                        const shouldPreserveStatus = pendingUpdate &&
                            (statusChanged || subStatusChanged) &&
                            (now - pendingUpdate.timestamp) < PENDING_STATUS_PRESERVE_MS;

                        if (!shouldPreserveStatus) return ac;
                        return {
                            ...ac,
                            status: pendingUpdate.status,
                            oth_sub_status: pendingUpdate.oth_sub_status
                        };
                    });
                    setAircraftList(protectedList);
                    setLoading(false);
                    break;
                }
                case 'aircraft_update':
                    // Real-time update for a specific aircraft
                    const updatedAircraft = lastMessage.data;
                    if (updatedAircraft.deleted) {
                        // Remove deleted aircraft
                        setAircraftList(prevList => prevList.filter(ac => ac.aircraft === updatedAircraft.aircraft));
                        // Clear any pending update for deleted aircraft
                        pendingStatusUpdatesRef.current.delete(updatedAircraft.aircraft);
                    } else {
                        const aircraftId = updatedAircraft.aircraft;
                        const pendingUpdate = pendingStatusUpdatesRef.current.get(aircraftId);
                        
                        // Check if there's a pending status update that's more recent
                        // If so, preserve the pending status to prevent reverting user's change
                        const statusChanged = pendingUpdate && pendingUpdate.status !== updatedAircraft.status;
                        const subStatusChanged = pendingUpdate && pendingUpdate.oth_sub_status !== updatedAircraft.oth_sub_status;
                        const shouldPreserveStatus = pendingUpdate &&
                            (statusChanged || subStatusChanged) &&
                            (Date.now() - pendingUpdate.timestamp) < PENDING_STATUS_PRESERVE_MS;
                        
                        // Update or add aircraft - properly merge with existing data
                        setAircraftList(prevList => {
                            const existingIndex = prevList.findIndex(ac => ac.aircraft === aircraftId);
                            if (existingIndex >= 0) {
                                // Update existing aircraft - merge updates with existing data
                                const newList = [...prevList];
                                const existingAircraft = newList[existingIndex];
                                
                                // If we have a pending status update, preserve it
                                const statusToUse = shouldPreserveStatus
                                    ? pendingUpdate.status
                                    : (updatedAircraft.status !== undefined ? updatedAircraft.status : existingAircraft.status);
                                const othSubStatusToUse = shouldPreserveStatus
                                    ? pendingUpdate.oth_sub_status
                                    : (updatedAircraft.oth_sub_status !== undefined ? updatedAircraft.oth_sub_status : existingAircraft.oth_sub_status);
                                
                                // Accept pm_status when the server sends it, including ''
                                // so a removed inspection clears the board. Keep the
                                // previous string only when the key is absent.
                                const pmStatus = Object.prototype.hasOwnProperty.call(updatedAircraft, 'pm_status')
                                    ? updatedAircraft.pm_status
                                    : existingAircraft.pm_status;
                                newList[existingIndex] = {
                                    ...existingAircraft,
                                    ...updatedAircraft,
                                    status: statusToUse,
                                    oth_sub_status: othSubStatusToUse,
                                    pm_status: pmStatus
                                };
                                return newList;
                            } else {
                                // Add new aircraft
                                return [...prevList, updatedAircraft];
                            }
                        });
                    }
                    break;
                default:
                    break;
            }
        }
    }, [lastMessage]);

    // Show WebSocket connection status
    useEffect(() => {
        if (wsError) {
            console.error('WebSocket error details:', wsError);
            if (wsError.includes('Authentication failed')) {
                setAlert({ show: true, message: 'Authentication expired. Please refresh the page to log in again.', type: 'error' });
            } else {
                setAlert({ show: true, message: `Real-time updates unavailable: ${wsError}. Data will refresh manually.`, type: 'warning' });
            }
        } else if (isConnected) {
            console.log('Real-time aircraft status updates connected');
            setAlert({ show: true, message: 'Real-time updates connected successfully!', type: 'success' });
        }
    }, [isConnected, wsError]);

    // Fetch aircraft data when WebSocket connection status changes
    useEffect(() => {
        // Only fetch initial data if WebSocket is not connected
        // WebSocket will provide the initial data when connected
        if (!isConnected) {
            fetchData();
        }
    }, [isConnected, fetchData]); // Only depend on WebSocket connection status

    useEffect(() => {
        api.get('/sites/')
            .then(res => {
                const sitesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setSites(sitesData);
            })
            .catch(err => {
                console.error("Error fetching sites:", err);
                setSites([]);
            });
        api.get('/tms/')
            .then(res => {
                const tmsData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setTmsList(tmsData);
            })
            .catch(err => {
                console.error("Error fetching TMS:", err);
                setTmsList([]);
            });
    }, []);

    useEffect(() => {
        fetchAdsbAircraft();
        const intervalId = setInterval(fetchAdsbAircraft, 5000);
        return () => clearInterval(intervalId);
    }, [fetchAdsbAircraft]);

    // Set up periodic refresh when WebSocket is not connected
    useEffect(() => {
        let intervalId;
        if (!isConnected) {
            // Refresh data every 30 seconds when WebSocket is not connected
            intervalId = setInterval(() => {
                console.log('Periodic refresh due to WebSocket disconnection');
                fetchData();
            }, 30000);
        }
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isConnected, fetchData]);

    const handleSaveDetails = (aircraftId, data) => {
        // Details modal should never update status; strip it defensively to avoid stale status overwrites.
        const { status: _ignoredStatus, ...detailsData } = data || {};

        // Optimistically update the UI immediately
        setAircraftList(prevList =>
            prevList.map(ac =>
                ac.aircraft === aircraftId ? { ...ac, ...detailsData } : ac
            )
        );

        api.patch(`/aircraft-status/${aircraftId}/`, detailsData)
            .then(() => {
                setAlert({ show: true, message: 'Aircraft details updated successfully.', type: 'success' });
            })
            .catch(() => {
                setAlert({ show: true, message: 'Failed to update details.', type: 'error' });
                // Revert optimistic update on error
                fetchData();
            });
    };

    const handleStatusChange = (aircraftId, selectedValue) => {
        const { status: newStatus, oth_sub_status: newOthSubStatus } = parseStatusSelection(selectedValue);
        // Determine if dependent fields should be cleared when status -> 'UP' or 'ACI'
        const isUp = newStatus === 'UP';
        const isACI = newStatus === 'ACI';
        const optimisticUpdate = (ac) => {
            if (ac.aircraft !== aircraftId) return ac;
            if (isUp) {
                return { ...ac, status: newStatus, oth_sub_status: null, reason: '', edd: null, ecd: null };
            }
            if (isACI) {
                return { ...ac, status: newStatus, oth_sub_status: null, last_fly_date: null };
            }
            return { ...ac, status: newStatus, oth_sub_status: newOthSubStatus };
        };

        // Track this as a pending update to prevent WebSocket from overwriting it
        const updateTimestamp = Date.now();
        pendingStatusUpdatesRef.current.set(aircraftId, {
            status: newStatus,
            oth_sub_status: newOthSubStatus,
            timestamp: updateTimestamp
        });

        // Optimistically update the UI immediately
        setAircraftList(prevList => prevList.map(optimisticUpdate));

        let payload;
        if (isUp) {
            payload = { status: newStatus, oth_sub_status: null, reason: '', edd: null, ecd: null };
        } else if (isACI) {
            payload = { status: newStatus, oth_sub_status: null, last_fly_date: null };
        } else {
            payload = { status: newStatus, oth_sub_status: newOthSubStatus };
        }

        api.patch(`/aircraft-status/${aircraftId}/`, payload)
            .then(() => {
                setAlert({ show: true, message: 'Status updated.', type: 'success' });
                // Clear pending update after successful API call
                // Keep it for a short time to handle delayed WebSocket messages
                setTimeout(() => {
                    pendingStatusUpdatesRef.current.delete(aircraftId);
                }, 5000); // Clear after 5 seconds
            })
            .catch(() => {
                setAlert({ show: true, message: 'Failed to update status.', type: 'error' });
                // Clear pending update on error
                pendingStatusUpdatesRef.current.delete(aircraftId);
                // Revert optimistic update on error
                fetchData();
            });
    };

    const handleUpdateEDD = () => {
        setLoading(true);
        api.post('/update-aircraft-edd/')
            .then(res => {
                setAlert({ show: true, message: 'Aircraft EDD updated successfully from NMCS PMCS data.', type: 'success' });
                // Refresh the data to show updated EDD values
                fetchData();
            })
            .catch(err => {
                console.error('Error updating EDD:', err);
                setAlert({ show: true, message: 'Failed to update EDD from NMCS PMCS data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const handleExportToExcel = async (selectedColumnIds) => {
        try {
            const calculateDaysSinceLFD = (lfd) => {
                if (!lfd) return '';
                const lfdDate = new Date(lfd);
                const today = new Date();
                const diffTime = today - lfdDate;
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                return diffDays;
            };

            const exportColumnDefinitions = {
                status: {
                    label: 'Status',
                    width: 10,
                    getValue: ac => ac.status || ''
                },
                buno: {
                    label: 'BUNO',
                    width: 12,
                    getValue: ac => ac.buno || ''
                },
                in_flight: {
                    label: 'In Flight',
                    width: 12,
                    getValue: ac => isAircraftInFlight(ac) ? 'In Flight' : ''
                },
                site: {
                    label: 'Site',
                    width: 10,
                    getValue: ac => ac.site || ''
                },
                reason: {
                    label: 'Reason/Remarks',
                    width: 40,
                    getValue: ac => ac.reason || ''
                },
                internal_comments: {
                    label: 'Internal Comments',
                    width: 40,
                    restricted: true,
                    getValue: ac => ac.internal_comments || ''
                },
                edd: {
                    label: 'EDD',
                    width: 12,
                    getValue: ac => ac.edd || (ac.status === 'AWP' ? 'EDDIW' : '')
                },
                ecd: {
                    label: 'ECD',
                    width: 12,
                    getValue: ac => ac.ecd || ''
                },
                lfd: {
                    label: 'LFD',
                    width: 12,
                    getValue: ac => ac.last_fly_date || ''
                },
                days_lfd: {
                    label: 'Days Since LFD',
                    width: 15,
                    restricted: true,
                    getValue: ac => calculateDaysSinceLFD(ac.last_fly_date)
                },
                pm_status: {
                    label: 'PM Status',
                    width: 30,
                    getValue: ac => ac.pm_status || ''
                },
                last_updated: {
                    label: 'Last Updated',
                    width: 15,
                    restricted: true,
                    getValue: ac => ac.last_updated_by_user || 'Report'
                }
            };

            const exportColumns = columns
                .filter(col => selectedColumnIds.includes(col.id))
                .map(col => ({ ...col, ...exportColumnDefinitions[col.id] }))
                .filter(col =>
                    col.getValue &&
                    col.id !== 'actions' &&
                    (!col.restricted || canViewRestricted)
                );

            if (exportColumns.length === 0) {
                setAlert({ show: true, message: 'Select at least one column to export.', type: 'warning' });
                return;
            }

            setShowExportColumns(false);
            setLoading(true);
            setAlert({ show: true, message: 'Generating Excel file...', type: 'info' });
            
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Aircraft Status');
            
            const headers = exportColumns.map(col => col.label);
            
            // Add header row
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true, size: 11 };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' }
            };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
            
            // Status color mapping (convert hex to ARGB format for Excel)
            const statusColors = {
                'UP': { bg: 'FF28A745', text: 'FFFFFFFF' }, // Green
                'XC': { bg: 'FF20C997', text: 'FFFFFFFF' }, // Teal
                'DN': { bg: 'FFDC3545', text: 'FFFFFFFF' }, // Red
                'DS': { bg: 'FFDC3545', text: 'FFFFFFFF' }, // Red
                'PM': { bg: 'FFFFC107', text: 'FF000000' }, // Yellow/Amber (black text)
                'FCF': { bg: 'FF8716BB', text: 'FFFFFFFF' }, // Purple
                'AWP': { bg: 'FF335D80', text: 'FFFFFFFF' }, // Blue
                'OTH': { bg: 'FF6C757D', text: 'FFFFFFFF' }, // Gray
                'DET': { bg: 'FF6C757D', text: 'FFFFFFFF' }, // Gray
                'AIMD': { bg: 'FF6C757D', text: 'FFFFFFFF' }, // Gray
                'ACI': { bg: 'FF6C757D', text: 'FFFFFFFF' }, // Gray
                'REI': { bg: 'FF6C757D', text: 'FFFFFFFF' }  // Gray
            };

            const columnIndexById = exportColumns.reduce((acc, col, index) => {
                acc[col.id] = index + 1;
                return acc;
            }, {});
            
            // Add data rows with color banding
            filteredAndSortedAircraft.forEach((ac, index) => {
                const rowData = exportColumns.map(col => col.getValue(ac));
                
                const row = worksheet.addRow(rowData);
                
                // Apply color banding (alternating row colors)
                // Even rows (index 0, 2, 4...) get light gray, odd rows stay white
                const isEvenRow = index % 2 === 0;
                const bandingColor = 'FFF2F2F2'; // Light gray for banding
                
                // Apply banding to all cells except Status and Days Since LFD (which have their own colors)
                row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                    // Skip Status and Days Since LFD columns as they have their own colors
                    if (colNumber !== columnIndexById.status && colNumber !== columnIndexById.days_lfd) {
                        if (isEvenRow) {
                            // Only apply if cell doesn't already have a fill (preserve any existing fills)
                            if (!cell.fill || !cell.fill.fgColor) {
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: bandingColor }
                                };
                            }
                        }
                    }
                });
                
                // Apply status color to Status column (column 2) - this will override banding
                const status = ac.status || '';
                if (columnIndexById.status && status && statusColors[status]) {
                    const statusCell = row.getCell(columnIndexById.status); // Status column
                    statusCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: statusColors[status].bg }
                    };
                    statusCell.font = { 
                        bold: true, 
                        color: { argb: statusColors[status].text } 
                    };
                    statusCell.alignment = { horizontal: 'center', vertical: 'middle' };
                }
                
                // Apply conditional formatting for Days Since LFD - this will override banding
                const daysSinceLFD = calculateDaysSinceLFD(ac.last_fly_date);
                if (columnIndexById.days_lfd && daysSinceLFD !== '') {
                    const daysCell = row.getCell(columnIndexById.days_lfd); // Days Since LFD column
                    if (daysSinceLFD >= 30 && daysSinceLFD < 60) {
                        daysCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFFFFF00' } // Yellow
                        };
                        daysCell.font = { bold: true, color: { argb: 'FF000000' } };
                    } else if (daysSinceLFD >= 60 && daysSinceLFD < 90) {
                        daysCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFDC3545' } // Red
                        };
                        daysCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    } else if (daysSinceLFD >= 90) {
                        daysCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FF000000' } // Black
                        };
                        daysCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    }
                } else if (columnIndexById.days_lfd && isEvenRow) {
                    // If no conditional formatting, apply banding to Days Since LFD column
                    const daysCell = row.getCell(columnIndexById.days_lfd);
                    daysCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: bandingColor }
                    };
                }
            });
            
            // Set column widths
            worksheet.columns = exportColumns.map(col => ({ width: col.width }));
            
            // Apply borders to all cells
            worksheet.eachRow((row, rowNumber) => {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });
            
            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Aircraft_Status_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setAlert({ show: true, message: 'Excel file exported successfully!', type: 'success' });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            return;
        }
        const calculateDaysSinceLFD = (lfd) => {
            if (!lfd) return '';
            const lfdDate = new Date(lfd);
            const today = new Date();
            const diffTime = today - lfdDate;
            return Math.floor(diffTime / (1000 * 60 * 60 * 24));
        };
        const getDaysLFDStyleClass = (days) => {
            if (days === '' || days === 'N/A') return '';
            if (days >= 30 && days < 60) return 'days-lfd-30-59';
            if (days >= 60 && days < 90) return 'days-lfd-60-89';
            if (days >= 90) return 'days-lfd-90-plus';
            return '';
        };
        const viewedSiteNames = [...new Set(
            filteredAndSortedAircraft.map((ac) => ac.site).filter(Boolean)
        )].sort((a, b) => String(a).localeCompare(String(b)));
        const printTitle = viewedSiteNames.length
            ? viewedSiteNames.join(', ')
            : (siteFilter || 'Aircraft Status Board');
        const doc = printWindow.document;
        doc.title = printTitle;
        const style = doc.createElement('style');
        style.textContent = [
            'body { font-family: Arial, sans-serif; }',
            'table { width: 100%; border-collapse: collapse; font-size: 8px; table-layout: fixed; }',
            'th, td { border: 1px solid #333; padding: 4px; text-align: left; word-wrap: break-word; }',
            'th { background-color: #f0f0f0; font-weight: bold; }',
            '.col-status { width: 5%; text-align: center; }',
            '.col-buno { width: 5%; text-align: center; }',
            '.col-reason { width: 55%; }',
            '.col-internal-comments { width: 25%; }',
            '.col-edd { width: 6%; }',
            '.col-ecd { width: 6%; }',
            '.col-days-lfd { width: 6%; text-align: center; }',
            '.status-badge { padding: 4px 8px; border-radius: 4px; color: white; font-weight: bold; display: inline-block; }',
            '.status-UP { background-color: #28a745; }',
            '.status-PMC { background-color: #538DD5; }',
            '.status-XC { background-color: #20c997; }',
            '.status-DN { background-color: #dc3545; }',
            '.status-DS { background-color: #dc3545; }',
            '.status-PM { background-color: #ffc107; color: #333; }',
            '.status-FCF { background-color: #8716bbff; }',
            '.status-AWP { background-color: #335d80ff; }',
            '.status-OTH { background-color: #6c757d; }',
            '.status-DET { background-color: #6c757d; }',
            '.status-AIMD { background-color: #6c757d; }',
            '.status-REI { background-color: #6c757d; }',
            '.status-ACI { background-color: #17a2b8; }',
            '.days-lfd-30-59 { background-color: #ffc107; color: #000; font-weight: bold; }',
            '.days-lfd-60-89 { background-color: #dc3545; color: #fff; font-weight: bold; }',
            '.days-lfd-90-plus { background-color: #000; color: #fff; font-weight: bold; }',
            'tbody tr:nth-child(even) td:not(.col-status):not(.col-days-lfd) { background-color: #f2f2f2; }',
            'html, body, table, th, td, .status-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
            '@media print {',
            '  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }',
            '  @page { size: landscape; margin: 0.5in; }',
            '  body { padding: 0; margin: 0; }',
            '  table { font-size: 8px; margin: 0; page-break-inside: auto; }',
            '  tr { page-break-inside: avoid; page-break-after: auto; }',
            '  th, td { padding: 2px; }',
            '}',
        ].join('\n');
        doc.head.appendChild(style);

        const allowedStatus = new Set(['UP', 'PMC', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'REI', 'ACI']);
        const textCell = (className, value) => {
            const cell = doc.createElement('td');
            cell.className = className;
            cell.textContent = value == null ? '' : String(value);
            return cell;
        };
        const headerCell = (className, label) => {
            const cell = doc.createElement('th');
            cell.className = className;
            cell.textContent = label;
            return cell;
        };

        const table = doc.createElement('table');
        const thead = doc.createElement('thead');
        const headRow = doc.createElement('tr');
        headRow.append(
            headerCell('col-buno', 'MODEX'),
            headerCell('col-status', 'Status'),
            headerCell('col-reason', 'Reason/Remarks'),
        );
        if (canViewRestricted) {
            headRow.appendChild(headerCell('col-internal-comments', 'Internal Comments'));
        }
        headRow.append(
            headerCell('col-edd', 'EDD'),
            headerCell('col-ecd', 'ECD'),
        );
        if (canViewRestricted) {
            headRow.appendChild(headerCell('col-days-lfd', 'Days Since LFD'));
        }
        thead.appendChild(headRow);
        table.appendChild(thead);

        const tbody = doc.createElement('tbody');
        filteredAndSortedAircraft.forEach((ac) => {
            const row = doc.createElement('tr');
            row.appendChild(textCell('col-buno', getAircraftModex(ac)));
            const statusCell = doc.createElement('td');
            statusCell.className = 'col-status';
            const badge = doc.createElement('span');
            const statusCode = allowedStatus.has(ac.status) ? ac.status : 'OTH';
            badge.className = 'status-badge status-' + statusCode;
            badge.textContent = ac.status || 'N/A';
            statusCell.appendChild(badge);
            row.appendChild(statusCell);
            row.appendChild(textCell('col-reason', ac.reason || ''));
            if (canViewRestricted) {
                row.appendChild(textCell('col-internal-comments', ac.internal_comments || ''));
            }
            const eddText = ac.edd || (ac.status === 'AWP' ? 'EDDIW' : '');
            row.appendChild(textCell('col-edd', eddText));
            row.appendChild(textCell('col-ecd', ac.ecd || ''));
            if (canViewRestricted) {
                const days = calculateDaysSinceLFD(ac.last_fly_date);
                const daysCell = textCell('col-days-lfd', days);
                const daysClass = getDaysLFDStyleClass(days);
                if (daysClass) {
                    daysCell.className = 'col-days-lfd ' + daysClass;
                }
                row.appendChild(daysCell);
            }
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        doc.body.replaceChildren(table);
        doc.title = printTitle;
        try {
            const sitePath = viewedSiteNames.length
                ? '/' + viewedSiteNames.map((name) => encodeURIComponent(String(name))).join(',')
                : '/Aircraft-Status-Board';
            printWindow.history.replaceState(null, '', sitePath);
        } catch (err) {
            // Some browsers keep about:blank in the footer if the address cannot be changed.
        }
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const canEdit = (groups.includes('Inspector') || groups.includes('Supervisor') || isStaff) && !groups.includes('Viewer');
    const canViewRestricted = groups.includes('Supervisor') || isStaff;
    const canExportExcel = groups.includes('Supervisor');

    // Helper function to calculate days since LFD and get style
    const getDaysLFDStyle = (lastFlyDate) => {
        if (!lastFlyDate) {
            return { style: styles.daysLfdNormal, days: '' };
        }

        // Parse date manually to avoid timezone issues
        const dateParts = lastFlyDate.split('T')[0].split('-');
        let flyDate;
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const day = parseInt(dateParts[2]);
            flyDate = new Date(year, month, day); // Create date in local timezone
        } else {
            flyDate = new Date(lastFlyDate);
        }
        
        const today = new Date();
        // Reset today to midnight for accurate day calculation
        const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const flyDateMidnight = new Date(flyDate.getFullYear(), flyDate.getMonth(), flyDate.getDate());
        const daysSinceFly = Math.floor((todayMidnight - flyDateMidnight) / (1000 * 60 * 60 * 24));

        // Critical: 90+ days
        if (daysSinceFly >= 90) {
            return { style: styles.daysLfdCritical, days: daysSinceFly };
        }
        // Highlighted: 60 to 89 days
        else if (daysSinceFly >= 60) {
            return { style: styles.daysLfdHighlighted, days: daysSinceFly };
        }
        // Warning: 30 to 59 days
        else if (daysSinceFly >= 30) {
            return { style: styles.daysLfdWarning, days: daysSinceFly };
        }
        
        // Normal: 0-29 days
        return { style: styles.daysLfdNormal, days: daysSinceFly };
    };

    // Column configuration state - load from localStorage or use defaults
    const [columns, setColumns] = useState(() => {
        // Calculate permissions first
        const _canEdit = (groups.includes('Inspector') || groups.includes('Supervisor') || isStaff) && !groups.includes('Viewer');
        const _canViewRestricted = groups.includes('Supervisor') || isStaff;
        
        const saved = localStorage.getItem('aircraftStatusBoard_columns');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Merge with defaults to handle new columns or permission changes
                const defaults = getDefaultColumns(_canViewRestricted, _canEdit);
                return defaults.map(defaultCol => {
                    const savedCol = parsed.find(c => c.id === defaultCol.id);
                    if (savedCol) {
                        // Only restore visibility if user still has permission
                        return {
                            ...defaultCol,
                            visible: savedCol.visible && (!defaultCol.restricted || (defaultCol.restricted && (_canViewRestricted || _canEdit)))
                        };
                    }
                    return defaultCol;
                });
            } catch (e) {
                console.error('Error loading column settings:', e);
            }
        }
        return getDefaultColumns(_canViewRestricted, _canEdit);
    });

    // Update columns when permissions change
    useEffect(() => {
        const defaults = getDefaultColumns(canViewRestricted, canEdit);
        setColumns(prev => {
            // Update visibility based on current permissions
            return defaults.map(defaultCol => {
                const prevCol = prev.find(c => c.id === defaultCol.id);
                if (prevCol) {
                    return {
                        ...defaultCol,
                        visible: prevCol.visible && (!defaultCol.restricted || (defaultCol.restricted && (canViewRestricted || canEdit)))
                    };
                }
                return defaultCol;
            });
        });
    }, [canViewRestricted, canEdit]);

    // Save column settings to localStorage
    const handleSaveColumnSettings = (newColumns) => {
        setColumns(newColumns);
        localStorage.setItem('aircraftStatusBoard_columns', JSON.stringify(newColumns));
        setAlert({ show: true, message: 'Column settings saved!', type: 'success' });
    };

    const inFlightAircraftKeys = useMemo(() => {
        return adsbAircraft.reduce((keys, aircraft) => {
            if (!isAdsbAircraftInFlight(aircraft)) return keys;
            getAdsbIdentifierCandidates(aircraft).forEach(candidate => keys.add(candidate));
            return keys;
        }, new Set());
    }, [adsbAircraft]);

    const isAircraftInFlight = useCallback((aircraft) => {
        const buno = normalizeAircraftIdentifier(aircraft.buno);
        return buno ? inFlightAircraftKeys.has(buno) : false;
    }, [inFlightAircraftKeys]);

    const filteredSites = useMemo(() => {
        if (!Array.isArray(sites)) return [];
        if (isStaff) return sites;
        return sites.filter(site => groups.includes(site.name));
    }, [sites, isStaff, groups]);

    const filteredTmsOptions = useMemo(() => {
        if (!Array.isArray(tmsList)) return [];
        const tmsGroups = getTmsGroups(groups, tmsList);
        if (isStaff || tmsGroups.length === 0) return tmsList;
        return tmsList.filter((tms) => tmsGroups.includes(tms.name));
    }, [tmsList, isStaff, groups]);

    const groupPolicyLabel = useMemo(
        () => getGroupPolicyLabel(groups, sites, tmsList),
        [groups, sites, tmsList]
    );

    useEffect(() => {
        const tmsGroups = getTmsGroups(groups, tmsList);
        if (!isStaff && tmsGroups.length === 1 && tmsFilter === '') {
            setTmsFilter(tmsGroups[0]);
        }
    }, [groups, isStaff, tmsList, tmsFilter]);


    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                {editingAircraft && <EditModal aircraft={editingAircraft} onClose={() => setEditingAircraft(null)} onSave={handleSaveDetails} theme={theme} />}
                {showColumnSettings && (
                    <ColumnSettingsModal
                        columns={columns}
                        onClose={() => setShowColumnSettings(false)}
                        onSave={handleSaveColumnSettings}
                        theme={theme}
                        canViewRestricted={canViewRestricted}
                        canEdit={canEdit}
                    />
                )}
                {showExportColumns && (
                    <ExportColumnsModal
                        columns={columns}
                        onClose={() => setShowExportColumns(false)}
                        onExport={handleExportToExcel}
                        theme={theme}
                        canViewRestricted={canViewRestricted}
                    />
                )}
                
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme}/>
                    <h1 style={styles.pageTitle}>Aircraft Status Board</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Real-time connection status indicator */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontSize: '12px',
                            color: isConnected ? '#28a745' : '#dc3545'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: isConnected ? '#28a745' : '#dc3545',
                                animation: isConnected ? 'pulse 2s infinite' : 'none'
                            }}></div>
                            {isConnected ? 'Live' : 'Offline'}
                            {!isConnected && (
                                <button 
                                    onClick={fetchData}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '10px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Refresh
                                </button>
                            )}
                        </div>
                        <div style={{ width: '20px' }}></div>
                    </div>
                </div>
                
                <div style={styles.header}>
                    <div>
                        <h1>Aircraft Status Board</h1>
                        {/* Group Policy Indicator */}
                        {!isStaff && groups.length > 0 && (
                            <div style={{
                                fontSize: '14px',
                                color: styles.subtleText,
                                marginTop: '5px',
                                fontStyle: 'italic'
                            }}>
                                Showing aircraft for: {groupPolicyLabel || groups.join(', ')}
                            </div>
                        )}
                        {isStaff && (
                            <div style={{
                                fontSize: '14px',
                                color: styles.subtleText,
                                marginTop: '5px',
                                fontStyle: 'italic'
                            }}>
                                Showing all aircraft (Staff access)
                            </div>
                        )}
                    </div>
                </div>

                {/* LFD Color Key */}
                {canViewRestricted && (
                    <div style={styles.lfdKey}>
                        <div style={styles.lfdKeyTitle}>Last Fly Date (LFD) Color Legend</div>
                        <div style={styles.lfdKeyGrid}>
                            <div style={styles.lfdKeyItem}>
                                <div style={{
                                    ...styles.lfdKeyColor,
                                    backgroundColor: styles.daysLfdNormal.backgroundColor || styles.cardBg,
                                    borderColor: styles.border
                                }}></div>
                                <div style={styles.lfdKeyText}>Normal: 0-29 days since last flight</div>
                            </div>
                            <div style={styles.lfdKeyItem}>
                                <div style={{
                                    ...styles.lfdKeyColor,
                                    backgroundColor: styles.daysLfdWarning.backgroundColor,
                                    borderColor: '#ff9800'
                                }}></div>
                                <div style={styles.lfdKeyText}>Warning: 30-59 days since last flight</div>
                            </div>
                            <div style={styles.lfdKeyItem}>
                                <div style={{
                                    ...styles.lfdKeyColor,
                                    backgroundColor: styles.daysLfdHighlighted.backgroundColor,
                                    borderColor: '#f44336'
                                }}></div>
                                <div style={styles.lfdKeyText}>Highlighted: 60-89 days since last flight</div>
                            </div>
                            <div style={styles.lfdKeyItem}>
                                <div style={{
                                    ...styles.lfdKeyColor,
                                    backgroundColor: styles.daysLfdCritical.backgroundColor,
                                    borderColor: '#9c27b0'
                                }}></div>
                                <div style={styles.lfdKeyText}>Critical: 90+ days since last flight</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search and Filter Controls */}
                <div style={styles.controlsContainer}>
                    <input
                        type="text"
                        placeholder="Search by BUNO..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setExcludeDepo(false);
                        }}
                        style={styles.filterSelect}
                    >
                        <option value="">All Statuses</option>
                        {Array.isArray(statusOptions) && statusOptions.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Sites</option>
                        {Array.isArray(filteredSites) && filteredSites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <select
                        value={tmsFilter}
                        onChange={(e) => setTmsFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All TMS</option>
                        {Array.isArray(filteredTmsOptions) && filteredTmsOptions.map((tms) => (
                            <option key={tms.id || tms.name} value={tms.name}>{tms.name}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => setShowColumnSettings(true)}
                        style={{
                            ...styles.button,
                            backgroundColor: '#6c757d',
                            color: 'white',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                        title="Customize column visibility and order"
                    >
                        Column Settings
                    </button>
                    <button
                        onClick={handlePrint}
                        style={{
                            ...styles.button,
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        Print
                    </button>
                    {canExportExcel && (
                        <button
                            onClick={() => setShowExportColumns(true)}
                            disabled={loading}
                            style={{
                                ...styles.button,
                                backgroundColor: '#17a2b8',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                        >
                            {loading ? 'Exporting...' : 'Export to Excel'}
                        </button>
                    )}
                    {canEdit && (
                        <button
                            onClick={handleUpdateEDD}
                            disabled={loading}
                            style={{
                                ...styles.button,
                                backgroundColor: '#28a745',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                        >
                            {loading ? 'Updating...' : 'Update EDD from NMCS PMCS'}
                        </button>
                    )}
                </div>

                {loading ? <Spinner /> : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    {columns.filter(col => col.visible).map(col => {
                                        if (col.id === 'status') {
                                            return <th key={col.id} style={styles.tableHeaderCell}>Status</th>;
                                        }
                                        if (col.id === 'buno') {
                                            return (
                                                <th key={col.id} style={styles.tableHeaderCell}>
                                                    <div style={styles.sortableHeader} onClick={() => handleSort('buno')}>
                                                        BUNO
                                                        {sortField === 'buno' && (
                                                            <span style={styles.sortIcon}>
                                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        }
                                        if (col.id === 'in_flight') {
                                            return <th key={col.id} style={styles.tableHeaderCell}>In Flight</th>;
                                        }
                                        if (col.id === 'site') {
                                            return <th key={col.id} style={styles.tableHeaderCell}>Site</th>;
                                        }
                                        if (col.id === 'reason') {
                                            return <th key={col.id} style={styles.tableHeaderCell}>Reason/Remarks</th>;
                                        }
                                        if (col.id === 'internal_comments') {
                                            return canViewRestricted ? <th key={col.id} style={styles.tableHeaderCell}>Internal Comments</th> : null;
                                        }
                                        if (col.id === 'edd') {
                                            return (
                                                <th key={col.id} style={styles.tableHeaderCell}>
                                                    <div style={styles.sortableHeader} onClick={() => handleSort('edd')}>
                                                        EDD
                                                        {sortField === 'edd' && (
                                                            <span style={styles.sortIcon}>
                                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{fontSize: '10px', color: styles.subtleText, fontWeight: 'normal'}}>
                                                        (Est. Delivery Date)
                                                    </div>
                                                </th>
                                            );
                                        }
                                        if (col.id === 'ecd') {
                                            return (
                                                <th key={col.id} style={styles.tableHeaderCell}>
                                                    <div style={styles.sortableHeader} onClick={() => handleSort('ecd')}>
                                                        ECD
                                                        {sortField === 'ecd' && (
                                                            <span style={styles.sortIcon}>
                                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        }
                                        if (col.id === 'lfd') {
                                            return (
                                                <th key={col.id} style={styles.tableHeaderCell}>
                                                    <div style={styles.sortableHeader} onClick={() => handleSort('last_fly_date')}>
                                                        LFD
                                                        {sortField === 'last_fly_date' && (
                                                            <span style={styles.sortIcon}>
                                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        }
                                        if (col.id === 'days_lfd') {
                                            return canViewRestricted ? <th key={col.id} style={styles.tableHeaderCell}>Days Since LFD</th> : null;
                                        }
                                        if (col.id === 'pm_status') {
                                            return <th key={col.id} style={styles.tableHeaderCell}>PM Status</th>;
                                        }
                                        if (col.id === 'last_updated') {
                                            return canViewRestricted ? <th key={col.id} style={styles.tableHeaderCell}>Last Updated</th> : null;
                                        }
                                        if (col.id === 'actions') {
                                            return canEdit ? <th key={col.id} style={styles.tableHeaderCell}>Actions</th> : null;
                                        }
                                        return null;
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(filteredAndSortedAircraft) && filteredAndSortedAircraft.map(ac => (
                                    <tr key={ac.buno} style={styles.tableRow}>
                                        {columns.filter(col => col.visible).map(col => {
                                            if (col.id === 'status') {
                                                return (
                                                    <td key={col.id} style={styles.tableCell}>
                                                        {canEdit ? (
                                                            <select
                                                                value={getStatusSelectValue(ac)}
                                                                onChange={(e) => handleStatusChange(ac.aircraft, e.target.value)}
                                                                style={{...styles.statusSelect, backgroundColor: (ac.status && styles.status[ac.status]) || '#6c757d'}}
                                                            >
                                                                <option value="" disabled>{getStatusSelectValue(ac) || ac.status || 'Set Status'}</option>
                                                                {renderStatusOptions()}
                                                            </select>
                                                        ) : (
                                                            <span style={{...styles.statusBadge, backgroundColor: (ac.status && styles.status[ac.status]) || '#6c757d'}}>{ac.status || 'N/A'}</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (col.id === 'buno') {
                                                return (
                                                    <td key={col.id} style={styles.tableCell}>
                                                        <span style={styles.buno}>{ac.buno}</span>
                                                    </td>
                                                );
                                            }
                                            if (col.id === 'in_flight') {
                                                const inFlight = isAircraftInFlight(ac);
                                                return (
                                                    <td key={col.id} style={styles.tableCell}>
                                                        {inFlight ? (
                                                            <Link
                                                                to={`/control-tower?aircraft=${encodeURIComponent(ac.buno)}`}
                                                                style={styles.inFlightBadge}
                                                                title={`View ${ac.buno} on Control Tower`}
                                                            >
                                                                In Flight
                                                            </Link>
                                                        ) : (
                                                            <span style={styles.notInFlightText}>-</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (col.id === 'site') {
                                                return <td key={col.id} style={styles.tableCell}>{ac.site}</td>;
                                            }
                                            if (col.id === 'reason') {
                                                return <td key={col.id} style={{...styles.tableCell, ...styles.reasonCell}}>{ac.reason}</td>;
                                            }
                                            if (col.id === 'internal_comments') {
                                                return canViewRestricted ? <td key={col.id} style={{...styles.tableCell, ...styles.internalCommentsCell}}>{ac.internal_comments}</td> : null;
                                            }
                                            if (col.id === 'edd') {
                                                return <td key={col.id} style={styles.tableCell}>{ac.edd || (ac.status === 'AWP' ? 'EDDIW' : '')}</td>;
                                            }
                                            if (col.id === 'ecd') {
                                                return <td key={col.id} style={styles.tableCell}>{ac.ecd}</td>;
                                            }
                                            if (col.id === 'lfd') {
                                                return <td key={col.id} style={styles.tableCell}>{ac.last_fly_date}</td>;
                                            }
                                            if (col.id === 'days_lfd') {
                                                return canViewRestricted ? (() => {
                                                    const { style, days } = getDaysLFDStyle(ac.last_fly_date);
                                                    return (
                                                        <td key={col.id} style={{...styles.tableCell, ...style}}>
                                                            {days !== '' ? days : ''}
                                                        </td>
                                                    );
                                                })() : null;
                                            }
                                            if (col.id === 'pm_status') {
                                                return <td key={col.id} style={{...styles.tableCell, ...styles.pmStatusCell}}>{ac.pm_status}</td>;
                                            }
                                            if (col.id === 'last_updated') {
                                                return canViewRestricted ? (
                                                    <td key={col.id} style={styles.tableCell}>
                                                        <div style={{fontSize: '12px', color: styles.subtleText}}>
                                                            {ac.last_updated_by_user || 'Report'}
                                                        </div>
                                                    </td>
                                                ) : null;
                                            }
                                            if (col.id === 'actions') {
                                                return canEdit ? (
                                                    <td key={col.id} style={styles.tableCell}>
                                                        <button 
                                                            onClick={() => setEditingAircraft(ac)} 
                                                            style={{...styles.button, padding: '5px 10px', fontSize: '12px', backgroundColor: styles.link, color: 'white'}}
                                                        >
                                                            Edit
                                                        </button>
                                                    </td>
                                                ) : null;
                                            }
                                            return null;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            </div>
        </div>
    );
}

export default AircraftStatusBoard;
